// Daily Focus — rotating-subject daily set (10 Qs), instant explanations,
// end-of-set recap. One set per (student, EAT date), surfaced from the
// Dashboard "Today's {subject}" card and the direct /daily-focus/today URL.
//
// Mirrors the Aptitude drill run page (pages/AptitudeDrill.tsx) but drives the
// dailyFocusAPI and shows the rotated subject instead of a path day. Kept as a
// separate page so the live aptitude drill is untouched.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DrillAnswerResponse, DrillQuestion } from '../services/aptitudeDrillAPI';
import {
  FocusCompleteResponse,
  FocusTodayResponse,
  dailyFocusAPI,
} from '../services/dailyFocusAPI';
import { track } from '../services/api';
import AIHelpPanel from '../components/MicroLearning/AIHelpPanel';
import FeedbackPrompt from '../components/feedback/FeedbackPrompt';
import { displayTopic } from '../config/topicDisplayNames';
import QuizCanvas, { ACCENTS } from '../components/ExamRun/QuizCanvas';
import { useTheme } from '../contexts/ThemeContext';

const colors = (dark: boolean) => ({
  primary: dark ? '#7cb3ff' : '#1d4ed8',
  border: dark ? '#262c35' : '#e2e8f0',
  bg: dark ? '#0f1216' : '#f8fafc',
  card: dark ? '#171b21' : 'white',
  backArrow: dark ? '#aab1ba' : '#475569',
  textPrimary: dark ? '#e6e8eb' : '#0f172a',
  textMuted: dark ? '#aab1ba' : '#64748b',
  correct: dark ? '#86c232' : '#16a34a',
  correctBg: dark ? 'rgba(134,194,50,0.12)' : '#f0fdf4',
  wrong: dark ? '#ff6b6b' : '#dc2626',
  wrongBg: dark ? 'rgba(255,107,107,0.15)' : '#fef2f2',
});

const DailyFocus: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = colors(dark);
  const [searchParams] = useSearchParams();
  // Optional ?subject= targets a specific subject (per-subject drill); omitted
  // → the daily rotation picks.
  const urlSubject = searchParams.get('subject') || undefined;

  const [focus, setFocus] = useState<FocusTodayResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [selected, setSelected] = useState<string | null>(null);
  const [lastAnswer, setLastAnswer] = useState<DrillAnswerResponse | null>(null);
  const [qStartedAt, setQStartedAt] = useState<number>(Date.now());
  const [recap, setRecap] = useState<FocusCompleteResponse | null>(null);
  const [showAIHelp, setShowAIHelp] = useState(false);
  const [consecutiveWrong, setConsecutiveWrong] = useState(0);

  const [currentIdx, setCurrentIdx] = useState(0);
  const initialResumeApplied = useRef(false);

  // -------------- load --------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await dailyFocusAPI.today(urlSubject);
        if (cancelled) return;
        setFocus(data);
        setQStartedAt(Date.now());
        track('daily_focus.opened', {
          subject: data.subject,
          status: data.status,
          answered: data.progress.answered,
          total: data.total_questions,
        });
        if (data.status === 'completed') {
          try {
            const c = await dailyFocusAPI.complete(data.subject);
            if (!cancelled) setRecap(c);
          } catch {
            /* non-critical — fall back to in-progress view */
          }
        }
      } catch (e: any) {
        const status = e?.response?.status;
        if (status === 401) {
          navigate('/login');
          return;
        }
        setLoadError(
          e?.response?.data?.detail ||
            "Couldn't load today's focus set. Try again in a moment.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, urlSubject]);

  // Resume at the first un-answered question on initial load (mid-session refresh).
  useEffect(() => {
    if (!focus || initialResumeApplied.current) return;
    const answered = new Set(focus.progress.answered_ids);
    const idx = focus.questions.findIndex(q => !answered.has(q.question_id));
    setCurrentIdx(idx === -1 ? focus.questions.length : idx);
    initialResumeApplied.current = true;
  }, [focus]);

  const currentQ: DrillQuestion | null =
    focus && currentIdx < focus.questions.length ? focus.questions[currentIdx] : null;

  const allDone = !!focus && currentIdx >= focus.total_questions;

  // -------------- handlers --------------
  const handleSelect = useCallback(
    async (letter: string) => {
      if (!currentQ || submitting || lastAnswer) return;
      setSelected(letter);
      setSubmitting(true);
      try {
        const elapsed = Math.max(0, Math.round((Date.now() - qStartedAt) / 1000));
        const resp = await dailyFocusAPI.answer(
          currentQ.question_id,
          letter,
          elapsed,
          focus?.subject,
        );
        setLastAnswer(resp);
        setConsecutiveWrong(prev => (resp.is_correct ? 0 : prev + 1));
        track('daily_focus.answered', {
          subject: focus?.subject,
          question_id: currentQ.question_id,
          topic: currentQ.topic,
          q_number: currentIdx + 1,
          total: focus?.total_questions ?? 10,
          is_correct: resp.is_correct,
          time_spent_seconds: elapsed,
          selected: letter,
        });
        setFocus(prev => (prev ? { ...prev, progress: resp.progress } : prev));
      } catch (e: any) {
        const detail = e?.response?.data?.detail || 'Could not submit answer';
        setSelected(null);
        setLoadError(detail);
      } finally {
        setSubmitting(false);
      }
    },
    [currentQ, submitting, lastAnswer, qStartedAt, focus, currentIdx],
  );

  const handleNext = useCallback(() => {
    setSelected(null);
    setLastAnswer(null);
    setQStartedAt(Date.now());
    setLoadError(null);
    setCurrentIdx(idx => idx + 1);
    setShowAIHelp(false);
  }, []);

  const finishIfDone = useCallback(async () => {
    if (!focus || recap) return;
    if (focus.progress.answered < focus.total_questions) return;
    try {
      const c = await dailyFocusAPI.complete(focus.subject);
      setRecap(c);
      track('daily_focus.completed', {
        subject: c.subject,
        score: c.score,
        total: c.total,
        weakest_topic: c.weakest_today?.topic,
      });
    } catch {
      /* swallow — student can retry by reloading */
    }
  }, [focus, recap]);

  useEffect(() => {
    if (allDone && !recap) finishIfDone();
  }, [allDone, recap, finishIfDone]);

  // -------------- views --------------
  if (loadError && !focus) {
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <div style={cardStyle(dark)}>
          <div style={{ color: COLORS.wrong, fontWeight: 600 }}>Something went wrong</div>
          <div style={{ marginTop: 6, color: COLORS.textMuted }}>{loadError}</div>
        </div>
      </Shell>
    );
  }

  if (!focus) {
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <div style={{ ...cardStyle(dark), color: COLORS.textMuted }}>Loading today's focus…</div>
      </Shell>
    );
  }

  if (recap) {
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <Recap
          recap={recap}
          onContinue={() => navigate('/dashboard')}
          onViewResults={() => navigate('/practice-history')}
        />
        <FeedbackPrompt surface="web_inflow" trigger="daily_focus_complete" />
      </Shell>
    );
  }

  if (!currentQ) {
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <div style={{ ...cardStyle(dark), color: COLORS.textMuted }}>Wrapping up…</div>
      </Shell>
    );
  }

  const qNumber = currentIdx + 1;
  const total = focus.total_questions;
  const topicDisp = currentQ.topic ? displayTopic(currentQ.topic) || currentQ.topic : '';
  // Spaced-review chip: a blended miss shows "🔁 you missed this before", then a
  // recovery/again note once answered.
  const reviewNote = currentQ.is_review
    ? lastAnswer
      ? lastAnswer.is_correct
        ? '✅ Recovered!'
        : '🔁 Seen before'
      : '🔁 You missed this before'
    : '';
  const contextLabel = [reviewNote, focus.subject, topicDisp, currentQ.year_ec || '']
    .filter(Boolean)
    .join(' · ');

  const sessionState = {
    timeOnComponentSeconds: Math.max(0, Math.round((Date.now() - qStartedAt) / 1000)),
    lastAnswer: selected || undefined,
    lastAnswerCorrect: lastAnswer ? lastAnswer.is_correct : undefined,
    consecutiveWrong,
    questionsAttempted: focus.progress.answered,
    questionsCorrect: focus.progress.correct,
  };

  return (
    <QuizCanvas
      accent={ACCENTS.daily}
      onBack={() => navigate('/dashboard')}
      answered={focus.progress.answered}
      qNumber={qNumber}
      total={total}
      contextLabel={contextLabel}
      questionText={currentQ.question_text}
      media={currentQ.media}
      options={currentQ.options}
      selected={selected}
      lastAnswer={lastAnswer}
      submitting={submitting}
      onSelect={handleSelect}
      onNext={handleNext}
      isLast={qNumber === total}
      questionId={currentQ.question_id}
      translateSurface="daily_focus_explanation"
      optionTestId={(l) => `focus-option-${l}`}
      feedbackTestId="daily-focus-feedback"
      runTestId="daily-focus-run"
      aiHelp={
        lastAnswer ? (
          <AIHelpPanel
            context="esslce"
            source="web_daily_focus"
            questionId={currentQ.question_id}
            questionText={currentQ.question_text}
            sessionState={sessionState}
            isOpen={showAIHelp}
            onToggle={() => setShowAIHelp(v => !v)}
          />
        ) : null
      }
    />
  );
};

// ----------- sub-components -----------

interface ShellProgress {
  current: number;
  total: number;
  currentIdx?: number;
}

const Shell: React.FC<{
  children: React.ReactNode;
  onBack: () => void;
  subject?: string;
  progress?: ShellProgress;
  meta?: React.ReactNode;
}> = ({ children, onBack, subject, progress, meta }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = colors(dark);
  return (
  <div style={{ minHeight: '100vh', background: COLORS.bg }}>
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        background: COLORS.card,
        borderBottom: `1px solid ${COLORS.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <button
        onClick={onBack}
        aria-label="Back"
        style={{
          background: 'transparent',
          border: 'none',
          padding: '6px 6px',
          cursor: 'pointer',
          color: COLORS.backArrow,
          fontSize: 18,
          lineHeight: 1,
        }}
      >
        ←
      </button>
      {progress ? (
        <DotStepper {...progress} />
      ) : (
        <h1 style={{ margin: 0, fontSize: '1rem', color: COLORS.textPrimary }}>
          {subject ? `Today's ${subject}` : 'Daily Focus'}
        </h1>
      )}
      <div style={{ flex: 1 }} />
      {meta}
    </header>
    <main style={{ maxWidth: 720, margin: '0 auto', padding: '12px 16px 80px' }}>{children}</main>
  </div>
  );
};

const DotStepper: React.FC<ShellProgress> = ({ current, total, currentIdx }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = colors(dark);
  return (
  <div
    role="progressbar"
    aria-valuenow={current}
    aria-valuemin={0}
    aria-valuemax={total}
    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
  >
    {Array.from({ length: total }).map((_, i) => {
      const isDone = i < current;
      const isCurrent = currentIdx !== undefined ? i === currentIdx : false;
      const size = isCurrent ? 10 : 8;
      return (
        <span
          key={i}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: isDone ? COLORS.primary : 'transparent',
            border: `1.5px solid ${
              isDone ? COLORS.primary : isCurrent ? COLORS.primary : (dark ? '#2d333b' : '#cbd5e1')
            }`,
            transition: 'all 180ms ease',
          }}
        />
      );
    })}
  </div>
  );
};

const Recap: React.FC<{
  recap: FocusCompleteResponse;
  onContinue: () => void;
  onViewResults: () => void;
}> = ({ recap, onContinue, onViewResults }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = colors(dark);
  const pct = recap.total > 0 ? Math.round((recap.score / recap.total) * 100) : 0;
  const [paused, setPaused] = useState(false);
  const pause = async () => {
    try {
      await dailyFocusAPI.snooze(3);
      setPaused(true);
    } catch {
      /* non-critical */
    }
  };
  return (
    <div style={cardStyle(dark)} data-testid="daily-focus-recap">
      <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.textPrimary }}>
        Today's {recap.subject}: {recap.score} / {recap.total}
      </div>
      <div style={{ marginTop: 4, color: COLORS.textMuted, fontSize: 14 }}>
        {pct}% — {recap.focus_date}
      </div>

      {recap.weakest_today && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: dark ? 'rgba(245,176,65,0.12)' : '#fef9c3',
            border: `1px solid ${dark ? 'rgba(245,176,65,0.35)' : '#fde047'}`,
            borderRadius: 10,
            color: dark ? '#f5c563' : '#713f12',
          }}
        >
          <strong>Today's weakest:</strong>{' '}
          {displayTopic(recap.weakest_today.topic) || recap.weakest_today.topic} —{' '}
          {recap.weakest_today.correct}/{recap.weakest_today.answered}
        </div>
      )}

      {Object.keys(recap.by_topic).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{ fontSize: 13, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}
          >
            By topic
          </div>
          {Object.entries(recap.by_topic).map(([topic, b]) => (
            <div
              key={topic}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '6px 0',
                borderBottom: `1px solid ${COLORS.border}`,
                fontSize: 14,
              }}
            >
              <span>{displayTopic(topic) || topic}</span>
              <span style={{ color: COLORS.textMuted }}>
                {b.correct}/{b.answered}
              </span>
            </div>
          ))}
        </div>
      )}

      {recap.next_step && (
        <div
          data-testid="daily-focus-next-step"
          style={{
            marginTop: 16,
            padding: 12,
            background: dark ? 'rgba(93,173,226,0.12)' : '#eff6ff',
            border: `1px solid ${COLORS.primary}`,
            borderRadius: 10,
            color: dark ? '#7cb3ff' : '#1e3a8a',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          <span style={{ marginRight: 6 }}>👉</span>
          {recap.next_step.label}
        </div>
      )}

      <button onClick={onContinue} style={primaryBtnStyle(dark)}>
        Back to Dashboard
      </button>

      <button
        data-testid="practice-history-link"
        onClick={onViewResults}
        style={{
          marginTop: 10,
          width: '100%',
          padding: '8px 14px',
          background: 'transparent',
          color: COLORS.textMuted,
          border: 'none',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        📚 My past results &amp; mocks ›
      </button>

      {paused ? (
        <div
          data-testid="daily-focus-snoozed"
          style={{ marginTop: 12, color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}
        >
          Reminders paused for 3 days. You can still practice anytime.
        </div>
      ) : (
        <button
          data-testid="daily-focus-snooze-btn"
          onClick={pause}
          style={{
            marginTop: 10,
            width: '100%',
            padding: '8px 14px',
            background: 'transparent',
            color: COLORS.textMuted,
            border: 'none',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          ⏸ Pause reminders for 3 days
        </button>
      )}
    </div>
  );
};

// ----------- inline styles -----------

const cardStyle = (dark: boolean): React.CSSProperties => ({
  background: dark ? '#171b21' : 'white',
  border: `1px solid ${colors(dark).border}`,
  borderRadius: 14,
  padding: 16,
  boxShadow: dark ? '0 1px 0 rgba(0,0,0,0.4)' : '0 1px 0 rgba(0,0,0,0.02)',
});

const metaStyle = (dark: boolean): React.CSSProperties => ({
  fontSize: 13,
  fontWeight: 600,
  color: colors(dark).textMuted,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '50vw',
});

// Keep the filled primary button on its saturated brand blue so white label
// text stays legible in both themes (the accent `primary` lightens for dark).
const primaryBtnStyle = (dark: boolean): React.CSSProperties => ({
  marginTop: 12,
  width: '100%',
  padding: '12px 16px',
  background: '#1d4ed8',
  color: 'white',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
});

export default DailyFocus;
